import { motion } from "framer-motion";
import { MapPin, Globe2 } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/site/primitives";
import { useI18n } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

export const About = () => {
  const { t } = useI18n();
  const CHAPTERS = [
    { no: "01", title: t("ab.c1.t"), body: t("ab.c1.b") },
    { no: "02", title: t("ab.c2.t"), body: t("ab.c2.b") },
    { no: "03", title: t("ab.c3.t"), body: t("ab.c3.b") },
  ];
  return (
    <section
      id="despre"
      data-testid="about-section"
      className="relative border-t border-white/10 bg-ink-800"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-12 lg:gap-10">
        {/* Left — sticky intro */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <SectionLabel index="[ 02 ]">{t("ab.label")}</SectionLabel>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl">
                {t("ab.h")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 font-body text-base leading-relaxed text-white/55">
                {t("ab.p")}
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10 flex gap-8 border-t border-white/10 pt-8">
                <div>
                  <div className="font-display text-3xl font-extrabold text-cyan-neon">
                    20+
                  </div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/40">
                    {t("ab.years")}
                  </div>
                </div>
                <div>
                  <div className="font-display text-3xl font-extrabold text-cyan-neon">
                    0
                  </div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/40">
                    {t("ab.lockin")}
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:gap-10">
                <div className="flex items-center gap-3" data-testid="about-hq">
                  <MapPin size={18} className="shrink-0 text-cyan-neon" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("ab.hq.label")}</div>
                    <div className="mt-0.5 font-display text-sm font-semibold text-white">{t("ab.hq.value")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-testid="about-coverage">
                  <Globe2 size={18} className="shrink-0 text-cyan-neon" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("ab.coverage.label")}</div>
                    <div className="mt-0.5 font-display text-sm font-semibold text-white">{t("ab.coverage.value")}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Right — numbered chapters */}
        <div className="lg:col-span-7 lg:col-start-6">
          <div className="flex flex-col gap-px bg-white/5">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.no}
                data-testid={`chapter-${c.no}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: EASE, delay: i * 0.08 }}
                className="group bg-ink-900 p-8 transition-colors duration-300 hover:bg-ink-700 sm:p-10"
              >
                <div className="flex items-baseline gap-5">
                  <span className="font-mono text-sm font-bold text-cyan-neon">
                    {c.no}
                  </span>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    {c.title}
                  </h3>
                </div>
                <p className="mt-4 pl-10 font-body text-base leading-relaxed text-white/55">
                  {c.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
